import React from 'react';
import { useNavigate } from 'react-router-dom';
import { SparklesIcon } from '@heroicons/react/24/outline';
import { useAISettings } from '@/services/aiCourses';
import { usePermissions } from '@/hooks/usePermissions';
import { PERM } from '@/auth/permissions';

/** The AI tab, which is where the switch lives. */
const AI_SETTINGS_PATH = '/settings?tab=ai';

interface AIDisabledNoticeProps {
  /** Close the chat panel on the way to Settings, so the page is not hidden. */
  onNavigate?: () => void;
}

/**
 * "AI is off, here is the switch" — inside Teri's panel, with a real button.
 *
 * Teri already explains the situation in words when asked, but words made the
 * member go and find Settings → AI themselves, which is the same hunt the
 * Free-plan banner exists to remove (Aleix, 2026-10-13). This puts the switch
 * one click away, and says it before the first question rather than after it.
 *
 * Shown only when the read says AI is genuinely off. Someone without
 * ``tenant.manage`` cannot flip it, so they get the sentence without a button
 * they would be refused — a dead control is worse than none.
 */
const AIDisabledNotice: React.FC<AIDisabledNoticeProps> = ({ onNavigate }) => {
  const { data: settings, isLoading } = useAISettings();
  const { can } = usePermissions();
  const navigate = useNavigate();

  // `available` is the system master switch — when that is off, the workspace
  // cannot turn anything on and pointing at Settings would be a lie.
  const offForThisWorkspace =
    !isLoading && settings?.available === true && settings?.enabled === false;
  if (!offForThisWorkspace) return null;

  const mayEnable = can(PERM.TENANT_MANAGE);

  const go = () => {
    onNavigate?.();
    navigate(AI_SETTINGS_PATH);
  };

  return (
    <div
      role="status"
      className="mx-3 mb-2 rounded-xl border border-warning-200 bg-warning-50 px-3 py-2.5 dark:border-warning-800 dark:bg-warning-900/20"
      data-testid="ai-disabled-notice"
    >
      <div className="flex items-start gap-2.5">
        <SparklesIcon
          aria-hidden="true"
          className="mt-0.5 h-4 w-4 shrink-0 text-warning-600 dark:text-warning-400"
        />
        <div className="min-w-0 flex-1">
          <p className="text-[13px] leading-snug text-warning-900 dark:text-warning-100">
            AI features are off for this workspace, so I can&apos;t think yet.
          </p>

          {mayEnable ? (
            <button type="button" onClick={go} className="btn btn-primary btn-sm mt-2">
              Turn on AI in Settings
            </button>
          ) : (
            <p className="mt-1 text-xs text-warning-800 dark:text-warning-200">
              Ask an Owner or Admin to switch them on in Settings → AI.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AIDisabledNotice;
